import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { GLASS_CHIP } from './glass';

/**
 * iOS-style segmented control: the selected segment sits on a glass pill that
 * slides between options. `layoutId` must be unique among controls on screen.
 */
const SegmentedControl = ({ options, value, onChange, layoutId, label, size = 'md', className = '' }) => (
  <div
    role="radiogroup"
    aria-label={label}
    className={`tw-inline-flex tw-shrink-0 tw-items-center tw-gap-0.5 tw-rounded-full tw-bg-slate-900/[0.05] tw-p-1 dark:tw-bg-white/[0.06] ${className}`}
  >
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          title={option.title}
          onClick={() => onChange(option.value)}
          className={`tw-relative tw-rounded-full tw-bg-transparent tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
            size === 'sm' ? 'tw-h-8 tw-px-2.5 tw-text-xs' : 'tw-h-9 tw-px-4 tw-text-sm'
          } ${selected ? 'tw-text-slate-900 dark:tw-text-white' : 'tw-text-slate-500 hover:tw-text-slate-800 dark:tw-text-slate-400 dark:hover:tw-text-white'}`}
        >
          {selected && (
            <motion.span
              layoutId={layoutId}
              transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              className={`tw-absolute tw-inset-0 tw-rounded-full ${GLASS_CHIP}`}
            />
          )}
          <span className="tw-relative">{option.label}</span>
        </button>
      );
    })}
  </div>
);

SegmentedControl.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    title: PropTypes.string,
  })).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  layoutId: PropTypes.string.isRequired,
  label: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
  className: PropTypes.string,
};

export default SegmentedControl;
